import { supabase } from "@/services/supabaseClient";

const LEGACY_STORAGE_KEY = "afrimed:favorite_doctor_ids";
const STORAGE_KEY_PREFIX = "afrimed:favorite_doctor_ids:v2";
const EVENT_NAME = "afrimed:doctor-favorites:changed";
const TABLE_NAME = "patient_doctor_favorites";
const FAVORITE_ID_KEYS = [
  "id",
  "doctorId",
  "doctor_id",
  "user_id",
  "doctor_user_id",
  "profile_id",
  "doctorProfileId",
  "doctor_profile_id",
];

let cachedIds = [];
let currentUserId = "";
let bootstrapped = false;
let hydrationPromise = null;

function isBrowser() {
  return typeof window !== "undefined";
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function collectIds(value, bucket, seen) {
  if (value == null) return;

  if (Array.isArray(value)) {
    value.forEach((entry) => collectIds(entry, bucket, seen));
    return;
  }

  if (typeof value === "object") {
    if (seen.has(value)) return;
    seen.add(value);

    FAVORITE_ID_KEYS.forEach((key) => {
      collectIds(value?.[key], bucket, seen);
    });
    collectIds(value?.favoriteIds, bucket, seen);
    collectIds(value?.doctor, bucket, seen);
    collectIds(value?.profile, bucket, seen);
    return;
  }

  const id = String(value || "").trim();
  if (id) bucket.add(id);
}

function normalizeIds(input) {
  const bucket = new Set();
  const seen = new WeakSet();
  collectIds(input, bucket, seen);
  return Array.from(bucket);
}

function getScopedStorageKey() {
  return `${STORAGE_KEY_PREFIX}:${currentUserId || "anonymous"}`;
}

function getScopedStorageKeyForUser(userId) {
  const scopedUserId = String(userId || "").trim();
  return `${STORAGE_KEY_PREFIX}:${scopedUserId || "anonymous"}`;
}

function parseStoredIds(raw) {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

function readIdsFromStorage() {
  if (!isBrowser()) return [];

  const next = new Set();
  const keys = [getScopedStorageKey(), LEGACY_STORAGE_KEY];
  keys.forEach((key) => {
    try {
      parseStoredIds(window.localStorage.getItem(key)).forEach((id) => next.add(id));
    } catch {
      // ignore storage errors
    }
  });
  return Array.from(next);
}

function readIdsFromStorageForUser(userId, { includeLegacy = false } = {}) {
  if (!isBrowser()) return [];

  const next = new Set();
  const keys = [getScopedStorageKeyForUser(userId)];
  if (includeLegacy) keys.push(LEGACY_STORAGE_KEY);

  keys.forEach((key) => {
    try {
      parseStoredIds(window.localStorage.getItem(key)).forEach((id) => next.add(id));
    } catch {
      // ignore storage errors
    }
  });

  return Array.from(next);
}

function emitFavoritesChanged() {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch {
    // ignore event failures
  }
}

function writeIds(ids, { clearLegacy = false } = {}) {
  const nextIds = Array.from(new Set(normalizeIds(ids)));
  const currentIds = cachedIds;
  const hasChanged =
    currentIds.length !== nextIds.length ||
    currentIds.some((id, index) => id !== nextIds[index]);

  cachedIds = nextIds;

  if (isBrowser()) {
    try {
      window.localStorage.setItem(getScopedStorageKey(), JSON.stringify(nextIds));
      if (clearLegacy) window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore persistence failures
    }
  }

  if (hasChanged) emitFavoritesChanged();
}

function expandFavoriteRows(rows) {
  const bucket = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const doctorUserId = String(row?.doctor_user_id || "").trim();
    const doctorProfileId = String(row?.doctor_profile_id || "").trim();
    if (doctorUserId) bucket.add(doctorUserId);
    if (doctorProfileId) bucket.add(doctorProfileId);
  });
  return Array.from(bucket);
}

async function resolveCurrentUserId() {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return String(user?.id || "").trim();
  } catch {
    return "";
  }
}

async function resolveDoctorRefs(input) {
  const candidateIds = normalizeIds(input).filter(isUuid);
  if (candidateIds.length === 0) return [];

  const refsByUserId = new Map();

  async function loadBy(column) {
    const { data, error } = await supabase
      .from("doctor_profiles")
      .select("id, user_id")
      .in(column, candidateIds);

    if (error) throw error;

    (Array.isArray(data) ? data : []).forEach((row) => {
      const doctorUserId = String(row?.user_id || "").trim();
      const doctorProfileId = String(row?.id || "").trim();
      if (!doctorUserId || !doctorProfileId) return;
      refsByUserId.set(doctorUserId, {
        doctorUserId,
        doctorProfileId,
      });
    });
  }

  await Promise.all([loadBy("id"), loadBy("user_id")]);
  return Array.from(refsByUserId.values());
}

async function fetchRemoteFavoriteRows(userId) {
  const scopedUserId = String(userId || "").trim();
  if (!scopedUserId) return [];

  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select("doctor_user_id, doctor_profile_id, created_at")
    .eq("patient_user_id", scopedUserId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

async function upsertRemoteFavorites(userId, input) {
  const scopedUserId = String(userId || "").trim();
  if (!scopedUserId) return;

  const refs = await resolveDoctorRefs(input);
  if (refs.length === 0) return;

  const rows = refs.map((ref) => ({
    patient_user_id: scopedUserId,
    doctor_user_id: ref.doctorUserId,
    doctor_profile_id: ref.doctorProfileId,
  }));

  const { error } = await supabase
    .from(TABLE_NAME)
    .upsert(rows, { onConflict: "patient_user_id,doctor_user_id" });

  if (error) throw error;
}

async function deleteRemoteFavorites(userId, input) {
  const scopedUserId = String(userId || "").trim();
  if (!scopedUserId) return;

  const refs = await resolveDoctorRefs(input);
  const doctorUserIds = Array.from(
    new Set(
      refs
        .map((ref) => String(ref?.doctorUserId || "").trim())
        .filter(Boolean)
    )
  );
  if (doctorUserIds.length === 0) return;

  const { error } = await supabase
    .from(TABLE_NAME)
    .delete()
    .eq("patient_user_id", scopedUserId)
    .in("doctor_user_id", doctorUserIds);

  if (error) throw error;
}

async function refreshRemoteFavorites(userId, { migrateLocal = false } = {}) {
  const scopedUserId = String(userId || "").trim();
  if (!scopedUserId) return cachedIds;

  const localIds = readIdsFromStorage();
  if (migrateLocal && localIds.length > 0) {
    try {
      await upsertRemoteFavorites(scopedUserId, localIds);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[doctorFavorites] local migration skipped:", error?.message || error);
    }
  }

  const rows = await fetchRemoteFavoriteRows(scopedUserId);
  const nextIds = expandFavoriteRows(rows);
  writeIds(nextIds, { clearLegacy: true });
  return nextIds;
}

async function handleAuthUserChange(nextUserId) {
  const scopedUserId = String(nextUserId || "").trim();
  const hasChanged = scopedUserId !== currentUserId;

  currentUserId = scopedUserId;
  cachedIds = readIdsFromStorage();
  if (hasChanged) emitFavoritesChanged();

  if (!scopedUserId) {
    hydrationPromise = null;
    return cachedIds;
  }

  if (!hasChanged && hydrationPromise) return hydrationPromise;

  hydrationPromise = refreshRemoteFavorites(scopedUserId, { migrateLocal: true }).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn("[doctorFavorites] remote hydration skipped:", error?.message || error);
    return cachedIds;
  });

  return hydrationPromise;
}

function ensureBootstrapped() {
  if (!isBrowser() || bootstrapped) return;
  bootstrapped = true;
  cachedIds = readIdsFromStorage();

  window.addEventListener("storage", (event) => {
    const key = String(event?.key || "").trim();
    if (
      key &&
      key !== LEGACY_STORAGE_KEY &&
      !key.startsWith(`${STORAGE_KEY_PREFIX}:`)
    ) {
      return;
    }
    cachedIds = readIdsFromStorage();
    emitFavoritesChanged();
  });

  supabase.auth.onAuthStateChange((_event, session) => {
    void handleAuthUserChange(session?.user?.id || "");
  });

  void resolveCurrentUserId().then((userId) => handleAuthUserChange(userId));
}

async function persistFavoriteMutation(userId, doctorId, nextValue) {
  const scopedUserId = String(userId || "").trim();
  if (!scopedUserId) return;

  try {
    if (nextValue) {
      await upsertRemoteFavorites(scopedUserId, doctorId);
    } else {
      await deleteRemoteFavorites(scopedUserId, doctorId);
    }
    await refreshRemoteFavorites(scopedUserId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[doctorFavorites] remote mutation skipped:", error?.message || error);
  }
}

export function getFavoriteDoctorIds() {
  ensureBootstrapped();
  return cachedIds;
}

export function buildFavoriteDoctorIds(...input) {
  return normalizeIds(input);
}

export function getFavoriteDoctorIdsForUser(userId) {
  return readIdsFromStorageForUser(userId);
}

export async function listFavoriteDoctorIdsForPatient(userId) {
  const scopedUserId = String(userId || "").trim();
  if (!scopedUserId) return [];

  try {
    const rows = await fetchRemoteFavoriteRows(scopedUserId);
    return expandFavoriteRows(rows);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[doctorFavorites] patient favorites fetch skipped:", error?.message || error);
    return [];
  }
}

export function isFavoriteDoctor(doctorId) {
  ensureBootstrapped();
  const ids = normalizeIds(doctorId);
  if (ids.length === 0) return false;
  const stored = new Set(cachedIds);
  return ids.some((id) => stored.has(id));
}

export function setFavoriteDoctor(doctorId, nextValue) {
  ensureBootstrapped();

  const ids = normalizeIds(doctorId);
  if (ids.length === 0) return false;

  const next = new Set(cachedIds);
  ids.forEach((id) => {
    if (nextValue) next.add(id);
    else next.delete(id);
  });
  writeIds(Array.from(next), { clearLegacy: !!currentUserId });

  const scopedUserId = currentUserId;
  if (scopedUserId) {
    void persistFavoriteMutation(scopedUserId, ids, !!nextValue);
  } else {
    void resolveCurrentUserId().then(async (userId) => {
      if (!userId) return;
      await handleAuthUserChange(userId);
      await persistFavoriteMutation(userId, ids, !!nextValue);
    });
  }

  return ids.some((id) => next.has(id));
}

export function subscribeDoctorFavorites(listener) {
  ensureBootstrapped();

  if (!isBrowser() || typeof listener !== "function") {
    return () => {};
  }

  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
